import React, { useCallback, useEffect, useState } from 'react';
import { Plus } from 'lucide-react';
import api from '../../services/api';
import Card from '../ui/Card';
import Button from '../ui/Button';
import { Input, Select } from '../ui/Input';
import Modal from '../ui/Modal';
import Skeleton from '../ui/Skeleton';
import { Table } from '../ui/Table';
import './AdminSetores.css';

const FORM_VAZIO = { nome: '', encarregado_nome: '' };

const TIPOS_DOCUMENTO = ['FURACAO', 'USINAGEM', 'PALETIZACAO'];
const TIPO_LABEL = {
  FURACAO: 'Furação',
  USINAGEM: 'Usinagem',
  PALETIZACAO: 'Paletização',
};

function VinculosSkeleton() {
  return (
    <div className="cw-vinculos-lista">
      {TIPOS_DOCUMENTO.map((tipo) => (
        <div className="cw-vinculos-item" key={tipo}>
          <Skeleton width="30%" height="14px" />
          <Skeleton width="220px" height="36px" />
        </div>
      ))}
    </div>
  );
}

function SetoresSkeleton() {
  return (
    <div className="cw-setores-skeleton">
      {[0, 1, 2].map((i) => (
        <div className="cw-setores-skeleton-row" key={i}>
          <Skeleton width="30%" height="14px" />
          <Skeleton width="30%" height="14px" />
          <Skeleton width="120px" height="32px" />
        </div>
      ))}
    </div>
  );
}

function AdminSetores() {
  const [setores, setSetores] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState(null);

  const [modal, setModal] = useState(null); // null | 'criar' | 'editar'
  const [form, setForm] = useState(FORM_VAZIO);
  const [setorEmEdicao, setSetorEmEdicao] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState(null);

  const [setorParaExcluir, setSetorParaExcluir] = useState(null);
  const [excluindo, setExcluindo] = useState(false);
  const [erroExclusao, setErroExclusao] = useState(null);

  const [vinculos, setVinculos] = useState(null);
  const [carregandoVinculos, setCarregandoVinculos] = useState(true);
  const [erroVinculos, setErroVinculos] = useState(null);
  const [salvandoVinculo, setSalvandoVinculo] = useState(null); // tipo em salvamento, ou null
  const [feedbackVinculo, setFeedbackVinculo] = useState(null); // { tipo, status: 'sucesso'|'erro', mensagem }

  const carregarSetores = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const { data } = await api.get('/setores');
      setSetores(data);
    } catch (err) {
      console.error('setores listar:', err);
      setErro('Falha ao carregar setores — tente atualizar a página');
    } finally {
      setCarregando(false);
    }
  }, []);

  const carregarVinculos = useCallback(async () => {
    setCarregandoVinculos(true);
    setErroVinculos(null);
    try {
      const { data } = await api.get('/setores/vinculos');
      setVinculos(data);
    } catch (err) {
      console.error('setores vinculos listar:', err);
      setErroVinculos('Falha ao carregar vínculos — tente atualizar a página');
    } finally {
      setCarregandoVinculos(false);
    }
  }, []);

  useEffect(() => {
    carregarSetores();
  }, [carregarSetores]);

  useEffect(() => {
    carregarVinculos();
  }, [carregarVinculos]);

  const handleVinculoChange = async (tipo, novoSetorIdStr) => {
    const setorId = novoSetorIdStr === '' ? null : Number(novoSetorIdStr);
    setSalvandoVinculo(tipo);
    setFeedbackVinculo(null);
    try {
      const { data } = await api.put(`/setores/vinculos/${tipo}`, { setor_id: setorId });
      setVinculos((prev) => prev.map((v) => (v.tipo_documento === tipo ? data : v)));
      setFeedbackVinculo({ tipo, status: 'sucesso' });
    } catch (err) {
      console.error('setores vinculos salvar:', err);
      setFeedbackVinculo({
        tipo,
        status: 'erro',
        mensagem: err.response?.data?.detail || 'Falha ao salvar vínculo',
      });
    } finally {
      setSalvandoVinculo(null);
      setTimeout(() => {
        setFeedbackVinculo((atual) => (atual?.tipo === tipo ? null : atual));
      }, 3000);
    }
  };

  const abrirCriar = () => {
    setForm(FORM_VAZIO);
    setSetorEmEdicao(null);
    setErroForm(null);
    setModal('criar');
  };

  const abrirEditar = (setor) => {
    setForm({ nome: setor.nome, encarregado_nome: setor.encarregado_nome });
    setSetorEmEdicao(setor);
    setErroForm(null);
    setModal('editar');
  };

  const fecharModal = () => {
    setModal(null);
    setErroForm(null);
  };

  const handleSalvar = async (e) => {
    e.preventDefault();
    setSalvando(true);
    setErroForm(null);

    const body = {
      nome: form.nome.trim(),
      encarregado_nome: form.encarregado_nome.trim(),
    };

    try {
      if (modal === 'criar') {
        const { data } = await api.post('/setores', body);
        setSetores((prev) => [...prev, data].sort((a, b) => a.nome.localeCompare(b.nome)));
      } else {
        const { data } = await api.put(`/setores/${setorEmEdicao.id}`, body);
        setSetores((prev) =>
          prev.map((s) => (s.id === data.id ? data : s)).sort((a, b) => a.nome.localeCompare(b.nome))
        );
      }
      fecharModal();
    } catch (err) {
      console.error('setores salvar:', err);
      setErroForm(err.response?.data?.detail || 'Falha ao salvar setor');
    } finally {
      setSalvando(false);
    }
  };

  const pedirExclusao = (setor) => {
    setErroExclusao(null);
    setSetorParaExcluir(setor);
  };

  const cancelarExclusao = () => {
    setSetorParaExcluir(null);
    setErroExclusao(null);
  };

  const confirmarExclusao = async () => {
    setExcluindo(true);
    setErroExclusao(null);
    try {
      await api.delete(`/setores/${setorParaExcluir.id}`);
      setSetores((prev) => prev.filter((s) => s.id !== setorParaExcluir.id));
      setSetorParaExcluir(null);
    } catch (err) {
      console.error('setores excluir:', err);
      setErroExclusao(err.response?.data?.detail || 'Falha ao excluir setor');
    } finally {
      setExcluindo(false);
    }
  };

  return (
    <div className="cw-admin-setores">
      <h1 className="cw-page-title">Setores</h1>
      <p className="cw-page-subtitle">Cadastro de setores e encarregados responsáveis pela conferência dos documentos.</p>

      <Card>
        <div className="cw-setores-header">
          <h3 className="cw-section-title">Setores</h3>
          <Button onClick={abrirCriar}>
            <Plus size={16} /> Novo setor
          </Button>
        </div>

        {carregando ? (
          <SetoresSkeleton />
        ) : erro ? (
          <p className="cw-setores-vazio" title={erro}>Não foi possível carregar os setores — tente atualizar.</p>
        ) : setores.length === 0 ? (
          <p className="cw-setores-vazio">Nenhum setor cadastrado ainda.</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Encarregado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {setores.map((setor) => (
                <tr key={setor.id}>
                  <td>{setor.nome}</td>
                  <td>{setor.encarregado_nome}</td>
                  <td className="cw-setores-acoes">
                    <Button variant="secondary" onClick={() => abrirEditar(setor)}>
                      Editar
                    </Button>
                    <Button variant="danger" onClick={() => pedirExclusao(setor)}>
                      Excluir
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card>
        <h3 className="cw-section-title">Conferência por documento</h3>
        <p className="cw-vinculos-hint">
          O encarregado do setor aparece como conferente nos PDFs deste tipo.
        </p>

        {carregandoVinculos ? (
          <VinculosSkeleton />
        ) : erroVinculos ? (
          <p className="cw-setores-vazio" title={erroVinculos}>
            Não foi possível carregar os vínculos — tente atualizar.
          </p>
        ) : (
          <div className="cw-vinculos-lista">
            {TIPOS_DOCUMENTO.map((tipo) => {
              const vinculo = vinculos?.find((v) => v.tipo_documento === tipo);
              const setorAtualId = vinculo?.setor?.id ?? '';
              const feedback = feedbackVinculo?.tipo === tipo ? feedbackVinculo : null;
              return (
                <div className="cw-vinculos-item" key={tipo}>
                  <span className="cw-vinculos-nome">{TIPO_LABEL[tipo]}</span>
                  <Select
                    value={setorAtualId}
                    disabled={salvandoVinculo === tipo || !setores || setores.length === 0}
                    onChange={(e) => handleVinculoChange(tipo, e.target.value)}
                  >
                    <option value="">—</option>
                    {(setores || []).map((s) => (
                      <option key={s.id} value={s.id}>{s.nome}</option>
                    ))}
                  </Select>
                  <span
                    className={`cw-vinculos-feedback${feedback?.status === 'erro' ? ' cw-vinculos-feedback--erro' : ''}`}
                  >
                    {salvandoVinculo === tipo
                      ? 'Salvando…'
                      : feedback?.status === 'sucesso'
                      ? 'Salvo'
                      : feedback?.status === 'erro'
                      ? feedback.mensagem
                      : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {modal && (
        <Modal onClose={fecharModal}>
          <h3 className="cw-section-title cw-setores-modal-titulo">
            {modal === 'criar' ? 'Novo setor' : `Editar — ${setorEmEdicao.nome}`}
          </h3>
          <form onSubmit={handleSalvar} className="cw-setores-form">
            <label className="cw-setores-campo">
              <span>Nome *</span>
              <Input
                required
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                placeholder="ex.: Produção"
                autoFocus
              />
            </label>
            <label className="cw-setores-campo">
              <span>Encarregado *</span>
              <Input
                required
                value={form.encarregado_nome}
                onChange={(e) => setForm({ ...form, encarregado_nome: e.target.value })}
                placeholder="Nome do encarregado"
              />
            </label>

            {erroForm && <p className="cw-setores-erro-form">{erroForm}</p>}

            <div className="cw-setores-modal-acoes">
              <Button type="button" variant="secondary" onClick={fecharModal}>
                Cancelar
              </Button>
              <Button type="submit" loading={salvando}>
                Salvar
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {setorParaExcluir && (
        <Modal onClose={cancelarExclusao}>
          <h3 className="cw-section-title cw-setores-modal-titulo">Excluir setor</h3>
          <p className="cw-setores-confirmacao-texto">
            Tem certeza que deseja excluir o setor <strong>{setorParaExcluir.nome}</strong>? Essa ação não
            pode ser desfeita.
          </p>

          {erroExclusao && <p className="cw-setores-erro-form">{erroExclusao}</p>}

          <div className="cw-setores-modal-acoes">
            <Button variant="secondary" onClick={cancelarExclusao}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmarExclusao} loading={excluindo}>
              Excluir
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default AdminSetores;
